import { Check, Loader2, RotateCcw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AppShell } from "../components/app-shell";
import { ResponsiveImage } from "../components/responsive-image";
import { getApiStatus, getApiVariants, startApiGeneration } from "../lib/api";
import { makeDemoVariants } from "../lib/demo";
import { loadProject, saveProject, saveVariants } from "../lib/storage";

const phases = [
  "Analiziramo prostor",
  "Stvaramo koncepte",
  "Prepoznajemo elemente",
  "Tražimo slične proizvode",
];

export function GeneratingPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const project = useMemo(() => loadProject(id), [id]);
  const [phase, setPhase] = useState(0);
  const [failed, setFailed] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!project || failed) return;
    project.status = "processing";
    saveProject(project);

    if (project.backend === "worker") {
      let cancelled = false;
      const timers = new Set<number>();
      const wait = (delay: number) =>
        new Promise<void>((resolve) => {
          const timer = window.setTimeout(() => {
            timers.delete(timer);
            resolve();
          }, delay);
          timers.add(timer);
        });

      const poll = async () => {
        try {
          await startApiGeneration(project.id);
          let attempt = 0;
          while (!cancelled) {
            const status = await getApiStatus(project.id);
            if (status.projectStatus === "completed") {
              const variants = await getApiVariants(project.id, project.kind);
              if (cancelled) return;
              saveVariants(variants);
              project.status = "completed";
              saveProject(project);
              void navigate(`/app/projects/${project.id}/results`, { replace: true });
              return;
            }
            if (status.projectStatus === "failed" || status.job?.status === "failed") {
              throw new Error("Provider nije uspio dovršiti prijedloge.");
            }
            setPhase(Math.min(phases.length - 1, attempt));
            const delay = document.hidden ? 5_000 : Math.min(2_500, 650 * 1.45 ** attempt);
            await wait(delay);
            attempt += 1;
          }
        } catch {
          if (!cancelled) {
            project.status = "failed";
            saveProject(project);
            setFailed(true);
          }
        }
      };
      void poll();
      return () => {
        cancelled = true;
        timers.forEach((timer) => window.clearTimeout(timer));
      };
    }

    const timers = phases.map((_, index) =>
      window.setTimeout(
        () => {
          setPhase(index);
          if (index === phases.length - 1) {
            const variants = makeDemoVariants(project.id, project.kind);
            saveVariants(variants);
            project.status = "completed";
            saveProject(project);
            window.setTimeout(() => {
              void navigate(`/app/projects/${project.id}/results`, { replace: true });
            }, 850);
          }
        },
        650 + index * 850,
      ),
    );
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [failed, navigate, project, retryKey]);

  if (!project) {
    return (
      <AppShell eyebrow="Projekt nije pronađen">
        <div className="empty-state">
          <ResponsiveImage name="empty-state" alt="" width={1024} height={1024} sizes="320px" />
          <h1>Ovaj projekt više nije dostupan.</h1>
          <p>Možda je obrisan ili mu je istekao rok čuvanja.</p>
          <Link className="button button--primary" to="/app/new">
            Pokreni novi projekt
          </Link>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell eyebrow="Demo generiranje" backTo="/app/new" backLabel="Uredi postavke">
      <section className="generating" aria-labelledby="generating-title" aria-live="polite">
        <div className="generating__visual">
          <img
            src={
              project.originalImageUrl ??
              (project.kind === "interior"
                ? "/images/interior-before-1024.webp"
                : "/images/exterior-after-1024.webp")
            }
            alt={`Fotografija projekta: ${project.space}`}
          />
          <div className="scan-line" aria-hidden="true" />
          <span className="generating__badge">
            <Loader2 className="spin" aria-hidden="true" />{" "}
            {project.backend === "worker" ? "Worker provider radi" : "Demo provider radi"}
          </span>
        </div>
        <div className="generating__content">
          <p className="section-label">Pet smjerova, bez lažnog postotka</p>
          <h1 id="generating-title">Pripremamo prijedloge za {project.space.toLowerCase()}.</h1>
          <p>Možeš zatvoriti ovu stranicu. Projekt i odabrane postavke nastavljaju se nakon osvježavanja.</p>
          <ol className="phase-list">
            {phases.map((label, index) => (
              <li key={label} className={index < phase ? "is-done" : index === phase ? "is-active" : ""}>
                <span>
                  {index < phase ? (
                    <Check aria-hidden="true" />
                  ) : index === phase ? (
                    <Loader2 className="spin" aria-hidden="true" />
                  ) : (
                    index + 1
                  )}
                </span>
                {label}
              </li>
            ))}
          </ol>
          {failed && (
            <div className="inline-error" role="alert">
              <strong>Nismo uspjeli dovršiti prijedloge.</strong>
              <p>Fotografija i postavke su sačuvane. Pokušaj ponovno.</p>
              <button
                className="button button--outline"
                type="button"
                onClick={() => {
                  setFailed(false);
                  setRetryKey((value) => value + 1);
                }}
              >
                <RotateCcw aria-hidden="true" /> Pokušaj ponovno
              </button>
            </div>
          )}
          <button className="test-failure-trigger" type="button" onClick={() => setFailed(true)}>
            Simuliraj grešku
          </button>
        </div>
      </section>
    </AppShell>
  );
}
